import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { IAuthUser } from '../types/IAuthUser';

interface IAuthContext {
  isSignedIn: boolean;
  user: IAuthUser | null;
  isAdmin: () => Promise<boolean>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isInitialized: boolean;
  getAccessToken: () => string | null;
  refreshAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<IAuthContext>({
  isSignedIn: false,
  user: null,
  isAdmin: () => {
    console.warn('isAdmin not implemented');
    return Promise.resolve(false);
  },
  login: () => {
    console.warn('login not implemented');
    return Promise.resolve();
  },
  logout: () => {
    console.warn('logout not implemented');
    return Promise.resolve();
  },
  isInitialized: false,
  getAccessToken: () => {
    console.warn('getAccessToken not implemented');
    return null;
  },
  refreshAccessToken: async () => {
    console.warn('refreshAccessToken not implemented');
    return null;
  }
});

interface IKeycloakAuthProviderProps {
  children: React.ReactNode;
}

export const KeycloakAuthProvider = ({ children }: IKeycloakAuthProviderProps) => {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useKeycloakAuth = () => useContext(AuthContext);

declare global {
  interface Window {
    Keycloak: any;
  }
}

const safeJwtDecode = <T extends object = { sub?: string }>(token: string | null): T | null => {
  if (!token) return null;
  try {
    return jwtDecode<T>(token);
  } catch (e) {
    console.error('Failed to decode token', e);
    return null;
  }
};

const useProvideAuth = (): IAuthContext => {
  const [user, setUser] = useState<IAuthUser | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [keycloak, setKeycloak] = useState<any | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const isWebEnvironment = typeof window !== 'undefined' && window.document;

  const storeTokens = useCallback((accessToken: string, idToken?: string, refreshToken?: string) => {
    try {
      localStorage.setItem('keycloak-access-token', accessToken);
      if (idToken) localStorage.setItem('keycloak-id-token', idToken);
      if (refreshToken) localStorage.setItem('keycloak-refresh-token', refreshToken);
    } catch (error) {
      console.error('[Auth Provider] Failed to store tokens in localStorage:', error);
    }
  }, []);

  const clearTokens = useCallback(() => {
    try {
      localStorage.removeItem('keycloak-access-token');
      localStorage.removeItem('keycloak-id-token');
      localStorage.removeItem('keycloak-refresh-token');
    } catch (error) {
      console.error('[Auth Provider] Failed to clear tokens from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    if (!isWebEnvironment) {
      console.log('Non-web environment detected, skipping Keycloak init.');
      return;
    }

    const initializeAuth = async () => {
      if (!document.querySelector('script[src*="/keycloak.js"]')) {
        const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || '/keycloak';
        const script = document.createElement('script');
        script.src = `${keycloakUrl}/js/keycloak.js`;
        script.async = true;
        script.onload = async () => {
          const kc = new window.Keycloak({
            url: keycloakUrl,
            realm: process.env.REACT_APP_KEYCLOAK_REALM ?? 'promete',
            clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID ?? 'mybrowserapp'
          });
          setKeycloak(kc);
          try {
            console.log('[Auth Provider] Initializing Keycloak with login-required...');
            const authenticated = await kc.init({
              onLoad: 'login-required',
              checkLoginIframe: false
            });
            console.log(`[Auth Provider] Keycloak init completed. Authenticated: ${authenticated}`);
            setIsSignedIn(authenticated);
            if (authenticated) {
              storeTokens(kc.token ?? '', kc.idToken ?? '', kc.refreshToken ?? '');
              const decodedToken = safeJwtDecode(kc.token);
              const userId = decodedToken?.sub ?? 'unknown-user';
              setUser({
                id: userId,
                accessToken: kc.token ?? '',
                idToken: kc.idToken ?? '',
                refreshToken: kc.refreshToken ?? ''
              });
            } else {
              clearTokens();
              setUser(null);
            }
          } catch (error) {
            console.error('[Auth Provider] Failed to initialize Keycloak', error);
            setIsSignedIn(false);
            setUser(null);
            clearTokens();
          } finally {
            setIsInitialized(true);
          }
        };
        script.onerror = () => {
          console.error('[Auth Provider] Failed to load Keycloak script');
        };
        document.body.appendChild(script);
      }
    };

    initializeAuth();
  }, [storeTokens, clearTokens, isWebEnvironment]);

  const isAdmin = async () => {
    if (!isSignedIn || !keycloak) {
      console.warn('[Auth Provider] isAdmin check skipped: Not signed in or Keycloak unavailable.');
      return false;
    }
    return keycloak.hasRealmRole('admin');
  };

  const login = async () => {
    if (!keycloak) {
      console.error('[Auth Provider] Keycloak not initialized for login');
      return;
    }
    try {
      await keycloak.login({ redirectUri: window.location.origin });
    } catch (error) {
      console.error('[Auth Provider] Failed to initiate login', error);
    }
  };

  const logout = useCallback(async () => {
    if (!keycloak) {
      console.error('[Auth Provider] Keycloak not initialized for logout');
      return;
    }
    try {
      clearTokens();
      await keycloak.logout({ redirectUri: window.location.origin });
      setUser(null);
      setIsSignedIn(false);
    } catch (error) {
      console.error('[Auth Provider] Failed to logout', error);
      clearTokens();
      setUser(null);
      setIsSignedIn(false);
    }
  }, [keycloak, clearTokens]);

  useEffect(() => {
    if (!isWebEnvironment || !keycloak || !isSignedIn) return;

    const refreshInterval = setInterval(async () => {
      try {
        const refreshed = await keycloak.updateToken(120);
        if (refreshed) {
          const decodedToken = safeJwtDecode(keycloak.token);
          const userId = decodedToken?.sub ?? 'unknown-user';
          storeTokens(keycloak.token ?? '', keycloak.idToken ?? '', keycloak.refreshToken ?? '');
          setUser({
            id: userId,
            accessToken: keycloak.token ?? '',
            idToken: keycloak.idToken ?? '',
            refreshToken: keycloak.refreshToken ?? ''
          });
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error('[Auth Provider] Failed to refresh token, logging out.', error);
        await logout();
      }
    }, 10000);

    return () => clearInterval(refreshInterval);
  }, [keycloak, isSignedIn, isWebEnvironment, logout, storeTokens]);

  useEffect(() => {
    if (!keycloak) return;
    keycloak.onTokenExpired = async () => {
      try {
        const refreshed = await keycloak.updateToken(180);
        if (refreshed) {
          const decodedToken = safeJwtDecode(keycloak.token);
          const userId = decodedToken?.sub ?? 'unknown-user';
          storeTokens(keycloak.token ?? '', keycloak.idToken ?? '', keycloak.refreshToken ?? '');
          setUser({
            id: userId,
            accessToken: keycloak.token ?? '',
            idToken: keycloak.idToken ?? '',
            refreshToken: keycloak.refreshToken ?? ''
          });
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch {
        console.error('[Auth Provider] Failed to refresh token via onTokenExpired, logging out.');
        logout();
      }
    };
  }, [keycloak, logout, storeTokens]);

  const getAccessToken = useCallback(() => {
    if (user?.accessToken) return user.accessToken;
    try {
      return localStorage.getItem('keycloak-access-token');
    } catch (error) {
      console.error('[Auth Provider] Failed to get access token from localStorage:', error);
      return null;
    }
  }, [user?.accessToken]);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      if (keycloak) {
        const refreshed = await keycloak.updateToken(180);
        if (refreshed) {
          const decodedToken = safeJwtDecode(keycloak.token);
          const userId = decodedToken?.sub ?? 'unknown-user';
          storeTokens(keycloak.token ?? '', keycloak.idToken ?? '', keycloak.refreshToken ?? '');
          setUser({
            id: userId,
            accessToken: keycloak.token ?? '',
            idToken: keycloak.idToken ?? '',
            refreshToken: keycloak.refreshToken ?? ''
          });
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return keycloak.token ?? user?.accessToken ?? null;
      }
      return user?.accessToken ?? null;
    } catch (error) {
      console.error('[Auth Provider] Explicit token refresh failed.', error);
      await logout();
      return null;
    }
  }, [keycloak, storeTokens, user?.accessToken, logout]);

  return {
    user,
    isSignedIn,
    isAdmin,
    login,
    logout,
    isInitialized,
    getAccessToken,
    refreshAccessToken
  };
};
